/**
 * Left summary rail of the differences-overview modal. Stacks the change-type
 * legend (with counts), the proportion bar, the no-overlap insight, and the
 * six model metrics as a compact before → after → Δ list. Modal-only — the 3D
 * rail keeps its own compact digest (IfcDiffSummary).
 */

import { AlertTriangle } from "lucide-react";

import { cn } from "@/lib/utils";
import type { BuildingIfcViewerSections } from "@/lib/i18n/types/buildings-sections/ifc-viewer";
import type { IfcModelDiff } from "@/stores/ifcModel/repository";
import {
  DIFF_METRICS,
  DIFF_SEGMENTS,
  DIFF_SEGMENT_SIGN,
  changeTypeLabel,
  fmtMetric,
  hasNoOverlap,
} from "./diffSummaryShared";

interface IfcDiffOverviewSidebarProps {
  diff: IfcModelDiff;
  tv: BuildingIfcViewerSections;
}

export function IfcDiffOverviewSidebar({ diff, tv }: IfcDiffOverviewSidebarProps) {
  const { summary, metric_deltas } = diff;
  const total = summary.added + summary.removed + summary.modified + summary.unchanged;

  return (
    <div className="ifc-glass-scroll flex w-[240px] shrink-0 flex-col gap-4 overflow-y-auto border-r border-border px-3 py-3">
      {/* Change-type legend (doubles as the proportion bar's key) */}
      <div className="flex flex-col gap-1.5">
        <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          {tv.compareSummaryTitle}
        </p>
        {DIFF_SEGMENTS.map((s) => (
          <div key={s.key} className="flex items-center gap-2 text-xs">
            <span className={cn("size-2 shrink-0 rounded-full", s.bar)} />
            <span className="text-muted-foreground">{changeTypeLabel(s.key, tv)}</span>
            <span className={cn("ml-auto tabular-nums", s.text)}>
              {DIFF_SEGMENT_SIGN[s.key]}
              {summary[s.key]}
            </span>
          </div>
        ))}
        {total > 0 && (
          <div className="mt-1 flex h-2 w-full overflow-hidden rounded-full bg-muted">
            {DIFF_SEGMENTS.map((s) => {
              const pct = (summary[s.key] / total) * 100;
              if (pct <= 0) return null;
              return <div key={s.key} className={s.bar} style={{ width: `${pct}%` }} />;
            })}
          </div>
        )}
      </div>

      {/* No-overlap insight */}
      {hasNoOverlap(summary) && (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-[11px] text-amber-700 dark:text-amber-300">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          <span>{tv.noOverlapInsight}</span>
        </div>
      )}

      {/* Model metrics: before → after with signed Δ */}
      <div className="flex flex-col">
        <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          {tv.compareMetricsTitle}
        </p>
        {DIFF_METRICS.map((m) => {
          const d = metric_deltas[m.key];
          const delta = d?.delta ?? null;
          return (
            <div key={m.key} className="py-1.5">
              <p className="truncate text-[10px] uppercase tracking-wide text-muted-foreground">
                {tv[m.labelKey]}
              </p>
              <p className="flex items-baseline justify-between gap-2 text-xs tabular-nums">
                <span>
                  {fmtMetric(d?.before ?? null)} → {fmtMetric(d?.after ?? null)}
                </span>
                {delta != null && delta !== 0 && (
                  <span
                    className={cn(
                      "font-medium",
                      delta > 0
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-rose-600 dark:text-rose-400"
                    )}
                  >
                    {delta > 0 ? "+" : ""}
                    {fmtMetric(delta)}
                  </span>
                )}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
